import * as Api from '@ViewModels';
import { IRichContentEditorState } from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ToolbarCustomSurvey } from '../../../../../models/UiUtils';
import { Button } from '../../../../components/Button';
import { ToggleAnimationWrapper } from '../../../../components/helpers/ToggleAnimationWrapper';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../../components/richContent/RichContentDocumentEditor';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { DonationSurveyFormData } from '../utils';
import { DonationReceiptPreviewModal } from './DonationReceiptPreviewModal';

const editorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	minHeight: 250,
	plugins: 'autolink',
	toolbar: ToolbarCustomSurvey,
};

export const DonationReceiptSettings: React.FC<{
	survey: Api.IDonationSurvey;
	impersonationContext?: Api.IImpersonationContext;
}> = ({ survey, impersonationContext }) => {
	const { control, watch } = useFormContext<DonationSurveyFormData>();
	const formValues = watch();
	const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

	const isReceiptEnabled = formValues.sendConfirmationEmail ?? true;
	const isArchived = !!survey?.archivedDate;
	// Preview renders the real receipt for a specific survey, so it needs a saved id.
	const canPreview = !!survey?.id;

	// SurveySettings force-mounts every tab; TinyMCE mis-renders when initialized inside
	// display:none, so defer mounting the editor until the section is on screen.
	const editorContainerRef = React.useRef<HTMLDivElement>(null);
	const [hasBeenVisible, setHasBeenVisible] = React.useState(false);
	React.useEffect(() => {
		const el = editorContainerRef.current;
		if (!el || hasBeenVisible) {
			return;
		}
		const observer = new IntersectionObserver(entries => {
			if (entries.some(entry => entry.isIntersecting)) {
				setHasBeenVisible(true);
				observer.disconnect();
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasBeenVisible]);

	return (
		<section className={css(styleSheet.notificationSection)}>
			<div className={css(styleSheet.notificationSettingsContainer)}>
				<FormFieldWrapper
					name='sendConfirmationEmail'
					control={control}
					title='Send donation receipt'
					description='Email a receipt to the donor after a successful donation.'
				>
					{({ onChange }) => (
						<Toggle
							id='edit-survey-donation-receipt-enabled-toggle'
							isOn={isReceiptEnabled}
							onToggleCheckChanged={onChange}
							uncheckedColor={grayIconFill}
							disabled={isArchived}
						/>
					)}
				</FormFieldWrapper>
			</div>

			<ToggleAnimationWrapper isVisible={isReceiptEnabled} duration={300}>
				<div className={css(styleSheet.receiptMessageControl)}>
					<span className={css(styleSheet.receiptMessageLabel)}>Receipt message</span>
					<div ref={editorContainerRef} className={css(styleSheet.receiptEditorContainer)}>
						{hasBeenVisible && (
							<Controller
								name='confirmationMessage'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<RichContentDocumentEditor
										config={editorConfig}
										contentState={formValues.confirmationMessage as IRichContentEditorState}
										onContentStateChanged={fieldProps.onChange}
										contentPlaceholderText='Leave blank to use the standard receipt.'
										readOnly={isArchived}
										readOnlyUseFullEditor={true}
									/>
								)}
							/>
						)}
					</div>
					<Button
						type='button'
						kind='reverse'
						className={css(styleSheet.receiptPreviewButton)}
						disabled={!canPreview}
						title={canPreview ? undefined : 'Save the survey to preview the receipt.'}
						onClick={() => setIsPreviewOpen(true)}
					>
						<span>Preview receipt</span>
					</Button>
				</div>
			</ToggleAnimationWrapper>

			{isPreviewOpen && (
				<DonationReceiptPreviewModal
					modalProps={{ isOpen: isPreviewOpen, onRequestClose: () => setIsPreviewOpen(false) }}
					surveyId={survey?.id}
					confirmationMessage={formValues.confirmationMessage?.getRawRichTextContent?.()}
					impersonationContext={impersonationContext}
				/>
			)}
		</section>
	);
};
